import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Star, ExternalLink } from "lucide-react";

/**
 * EmbedBusiness — standalone iframe-friendly widget that any business can drop
 * on their own site. Renders an auto-rotating photo slider, the business name,
 * star rating, and a "View on Servrevu" CTA linking back to the profile.
 *
 * Zero navbar/footer, transparent background (so hosts can inherit their own
 * page color), responsive to any iframe dimension. Query params:
 *
 *   theme    light | dark          — default: light
 *   interval milliseconds          — default: 4000
 *   rounded  0 | 1                 — default: 1 (soft corners)
 *   showcta  0 | 1                 — default: 1
 */
export default function EmbedBusiness() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const theme = params.get("theme") === "dark" ? "dark" : "light";
  const interval = Math.max(1200, parseInt(params.get("interval") || "4000", 10));
  const rounded = params.get("rounded") !== "0";
  const showCta = params.get("showcta") !== "0";

  const [biz, setBiz] = useState(null);
  const [customerPhotos, setCustomerPhotos] = useState([]);
  const [error, setError] = useState(null);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  // Host page shouldn't inherit any framework CSS from us — clean the body.
  useEffect(() => {
    const prevBg = document.body.style.background;
    const prevMargin = document.body.style.margin;
    document.body.style.background = "transparent";
    document.body.style.margin = "0";
    return () => {
      document.body.style.background = prevBg;
      document.body.style.margin = prevMargin;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [detail, photos] = await Promise.all([
          api.get(`/businesses/${id}`),
          api.get(`/businesses/${id}/customer-photos`).catch(() => ({ data: { items: [] } })),
        ]);
        if (!alive) return;
        setBiz(detail.data);
        const items = Array.isArray(photos.data?.items) ? photos.data.items : Array.isArray(photos.data) ? photos.data : [];
        setCustomerPhotos(items);
      } catch (e) {
        if (alive) setError("This listing is unavailable.");
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const slides = useMemo(() => {
    if (!biz) return [];
    const out = [];
    (biz.gallery || []).forEach((g) => {
      const url = typeof g === "string" ? g : (g?.data_url || g?.url);
      if (url) out.push({ url, caption: "" });
    });
    (customerPhotos || []).slice(0, 20).forEach((p) => {
      const url = p?.data_url || p?.url;
      if (url) out.push({ url, caption: p?.caption || "" });
    });
    if (!out.length && biz.logo_data_url) out.push({ url: biz.logo_data_url, caption: "" });
    return out;
  }, [biz, customerPhotos]);

  useEffect(() => {
    if (slides.length <= 1) return;
    timer.current && clearInterval(timer.current);
    timer.current = setInterval(() => {
      setIdx((i) => (i + 1) % slides.length);
    }, interval);
    return () => timer.current && clearInterval(timer.current);
  }, [slides.length, interval]);

  if (error) {
    return (
      <div data-testid="embed-error" style={styles.errorWrap(theme)}>
        {error}
      </div>
    );
  }
  if (!biz) {
    return <div data-testid="embed-loading" style={styles.loadingWrap(theme)}>Loading…</div>;
  }

  const profileUrl = `${window.location.origin}/businesses/${id}`;
  const active = slides[idx] || null;
  const rating = Number(biz.rating_avg || 0).toFixed(1);
  const reviewCount = biz.rating_count || 0;

  return (
    <div
      data-testid="embed-widget-root"
      style={{
        ...styles.root(theme, rounded),
        // Fill whatever the iframe gives us — the host controls dimensions.
        width: "100vw",
        height: "100vh",
      }}
    >
      <div style={styles.imageWrap}>
        {active ? (
          <img
            src={active.url}
            alt={biz.name}
            data-testid={`embed-slide-${idx}`}
            style={{
              ...styles.image,
              // Cross-fade between slides
              opacity: 1,
              transition: "opacity 500ms ease-in-out",
            }}
            key={idx}
          />
        ) : (
          <div style={styles.emptyPhoto(theme)}>
            <span style={{ fontSize: 12, opacity: 0.6 }}>No photos yet</span>
          </div>
        )}
        <div style={styles.imageOverlay} />
      </div>

      <a
        href={profileUrl}
        target="_top"
        rel="noopener"
        data-testid="embed-cta-link"
        style={styles.cardLink}
      >
        <div style={styles.cardOverlay(theme)}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={styles.title(theme)} data-testid="embed-business-name">{biz.name}</div>
              <div style={styles.subline(theme)}>
                <Star size={12} style={{ marginRight: 4, fill: "#fbbf24", stroke: "#fbbf24" }} />
                <span data-testid="embed-rating">{rating}</span>
                <span style={{ opacity: 0.7, marginLeft: 4 }}>({reviewCount} review{reviewCount === 1 ? "" : "s"})</span>
                {biz.city ? <span style={{ opacity: 0.7, marginLeft: 8 }}>· {biz.city}{biz.state ? `, ${biz.state}` : ""}</span> : null}
              </div>
            </div>
            {showCta ? (
              <div style={styles.ctaPill(theme)} data-testid="embed-cta-pill">
                View on Servrevu <ExternalLink size={11} style={{ marginLeft: 4 }} />
              </div>
            ) : null}
          </div>
          {slides.length > 1 ? (
            <div style={styles.dots} data-testid="embed-slide-dots">
              {slides.map((s, i) => (
                <span
                  key={`dot-${i}-${(s.url || "").slice(-12)}`}
                  style={{
                    ...styles.dot(theme),
                    opacity: i === idx ? 1 : 0.35,
                    transform: i === idx ? "scale(1.15)" : "scale(1)",
                  }}
                />
              ))}
            </div>
          ) : null}
        </div>
      </a>
    </div>
  );
}

const light = {
  panelBg: "rgba(255,255,255,0.94)",
  panelFg: "#111827",
  subFg: "#4b5563",
  ctaBg: "#B31E1E",
  ctaFg: "#ffffff",
  dot: "#111827",
  border: "rgba(0,0,0,0.08)",
};
const dark = {
  panelBg: "rgba(17,24,39,0.92)",
  panelFg: "#f9fafb",
  subFg: "#d1d5db",
  ctaBg: "#ffffff",
  ctaFg: "#111827",
  dot: "#f9fafb",
  border: "rgba(255,255,255,0.12)",
};
const palette = (t) => (t === "dark" ? dark : light);

const styles = {
  root: (t, rounded) => ({
    position: "relative",
    background: t === "dark" ? "#0b0f19" : "#f9fafb",
    color: palette(t).panelFg,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif",
    overflow: "hidden",
    borderRadius: rounded ? 12 : 0,
    boxShadow: rounded ? "0 4px 24px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)" : "none",
    boxSizing: "border-box",
  }),
  imageWrap: { position: "absolute", inset: 0, overflow: "hidden" },
  image: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  imageOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0) 100%)",
    pointerEvents: "none",
  },
  emptyPhoto: (t) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: t === "dark" ? "#1f2937" : "#e5e7eb",
    color: palette(t).subFg,
  }),
  cardLink: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    textDecoration: "none",
    color: "inherit",
    cursor: "pointer",
  },
  cardOverlay: (t) => ({
    background: palette(t).panelBg,
    borderTop: `1px solid ${palette(t).border}`,
    padding: "10px 12px 12px",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  }),
  title: (t) => ({
    fontSize: 15,
    fontWeight: 600,
    color: palette(t).panelFg,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    letterSpacing: "-0.01em",
  }),
  subline: (t) => ({
    fontSize: 11,
    marginTop: 2,
    color: palette(t).subFg,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  }),
  ctaPill: (t) => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: palette(t).ctaBg,
    color: palette(t).ctaFg,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
    flexShrink: 0,
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  }),
  dots: { marginTop: 8, display: "flex", gap: 4, justifyContent: "center" },
  dot: (t) => ({
    width: 6,
    height: 6,
    borderRadius: 999,
    background: palette(t).dot,
    display: "inline-block",
    transition: "all 300ms",
  }),
  loadingWrap: (t) => ({
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: t === "dark" ? "#0b0f19" : "#f9fafb",
    color: palette(t).subFg,
    fontFamily: "-apple-system, BlinkMacSystemFont, system-ui, sans-serif",
    fontSize: 13,
  }),
  errorWrap: (t) => ({
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: t === "dark" ? "#0b0f19" : "#f9fafb",
    color: palette(t).subFg,
    fontFamily: "-apple-system, BlinkMacSystemFont, system-ui, sans-serif",
    fontSize: 13,
    padding: 24,
    textAlign: "center",
  }),
};
